/**
 * Generic field-level diff between two revision snapshots (or a snapshot and
 * the live question). For each top-level field that differs, renders a
 * before/after block. Nested arrays/objects are JSON-stringified for display.
 */

import { useMemo } from 'react';

interface QuestionRevisionDiffProps {
  /** Earlier snapshot (e.g. older revision) */
  before: Record<string, unknown> | null;
  /** Later snapshot (e.g. live question or newer revision) */
  after: Record<string, unknown>;
  /** Labels for the two columns (default: "Revision" / "Live") */
  beforeLabel?: string;
  afterLabel?: string;
}

const IGNORE_KEYS = new Set([
  // Cosmos system fields
  '_rid', '_self', '_etag', '_attachments', '_ts', '_schemaVersion',
  // Document identity
  'id',
  // Immutable provenance — set once, never edited
  'importBatchId', 'sourceImportId', 'teacherId',
  // Audit envelope (already shown in the row metadata)
  'updatedAt', 'updatedBy', 'createdAt', 'createdBy',
  // Synthetic / computed
  'version', 'questionTextHash', 'questionCoreHash',
]);

/**
 * Two values are considered "the same" if they're strictly equal, JSON-equal,
 * or both empty. "Empty" means undefined, null, '', [], or {}.
 * This kills false positives from form state ↔ document shape mismatches:
 *   - form state stores cleared fields as null; document omits them
 *   - form initialises optional arrays to [], document leaves them undefined
 */
function isEmpty(v: unknown): boolean {
  if (v === undefined || v === null || v === '') return true;
  if (Array.isArray(v) && v.length === 0) return true;
  if (typeof v === 'object' && Object.keys(v as object).length === 0) return true;
  return false;
}

function valuesEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (isEmpty(a) && isEmpty(b)) return true;
  return JSON.stringify(a) === JSON.stringify(b);
}

function stringify(v: unknown): string {
  if (v === undefined) return '(unset)';
  if (v === null) return '(null)';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

export function QuestionRevisionDiff({
  before,
  after,
  beforeLabel = 'Revision',
  afterLabel = 'Live',
}: QuestionRevisionDiffProps) {
  const diffRows = useMemo(() => {
    const beforeObj = before ?? {};
    const keys = new Set<string>([
      ...Object.keys(beforeObj),
      ...Object.keys(after),
    ]);
    const rows: { key: string; before: unknown; after: unknown }[] = [];
    for (const key of keys) {
      if (IGNORE_KEYS.has(key)) continue;
      const a = (beforeObj as Record<string, unknown>)[key];
      const b = after[key];
      if (valuesEqual(a, b)) continue;
      rows.push({ key, before: a, after: b });
    }
    rows.sort((x, y) => x.key.localeCompare(y.key));
    return rows;
  }, [before, after]);

  if (diffRows.length === 0) {
    return (
      <div className="qh-diff-empty">
        No field differences.
      </div>
    );
  }

  return (
    <div className="qh-diff">
      <div className="qh-diff-header">
        <div className="qh-diff-cell-key">Field</div>
        <div className="qh-diff-cell-before">{beforeLabel}</div>
        <div className="qh-diff-cell-after">{afterLabel}</div>
      </div>
      {diffRows.map(row => (
        <div key={row.key} className="qh-diff-row">
          <div className="qh-diff-cell-key">{row.key}</div>
          <pre className="qh-diff-cell-before">{stringify(row.before)}</pre>
          <pre className="qh-diff-cell-after">{stringify(row.after)}</pre>
        </div>
      ))}
    </div>
  );
}
